import React, { useEffect, useCallback, useState, useRef } from 'react';
import '../Booking.css';
import Spinner from '../../Spinner/Spinner';
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaCopy,
  FaDollarSign,
  FaExternalLinkAlt,
  FaFileUpload,
  FaInfoCircle,
  FaPhoneAlt,
  FaPlus,
  FaSave,
  FaSearch,
  FaTimes,
  FaTrashAlt,
  FaUser,
} from 'react-icons/fa';
import { fetchProcedimentos, getFileFromS3 } from '../../api';
import { useNavigate } from 'react-router-dom';
import { parseCurrencyValue } from '../../../utils/currency';

const formatStatusTimestamp = (value) => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  const pad = (part) => String(part).padStart(2, '0');

  return [
    `${pad(parsed.getDate())}/${pad(parsed.getMonth() + 1)}/${pad(parsed.getFullYear()).slice(-2)}`,
    `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
  ].join(' ');
};

const parseDate = (value) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDuration = (milliseconds) => {
  if (!Number.isFinite(milliseconds)) {
    return '';
  }

  const totalMinutes = Math.round(Math.abs(milliseconds) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

function EditBookingModal({ 
  showModal, 
  formData, 
  onClose, 
  onUpdate, 
  onUpdateStatus,
  isLoading,
  professionals, 
  selectedBooking 
}) {
  const [procedures, setProcedures] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSubProcedures, setShowSubProcedures] = useState(false);
  const [currentProcedure, setCurrentProcedure] = useState(null);
  const [downloadingProof, setDownloadingProof] = useState(false);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [bookingAgents, setBookingAgents] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [otherChannelText, setOtherChannelText] = useState('');
  const navigate = useNavigate();
  const [isPaymentDropdownOpen, setIsPaymentDropdownOpen] = useState(false);
  const [isFlagsDropdownOpen, setIsFlagsDropdownOpen] = useState(false);
  const paymentDropdownRef = useRef(null);
  const flagsDropdownRef = useRef(null);
  const getProofFileName = () => {
    if (formData?.file_name) return formData.file_name;
    if (formData?.file_key) return decodeURIComponent(formData.file_key.split('/').pop() || 'comprovante');
    return 'comprovante';
  };

  const getProofMimeType = () => {
    const extension = getProofFileName().split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return 'application/pdf';
    if (extension === 'png') return 'image/png';
    if (extension === 'jpg' || extension === 'jpeg') return 'image/jpeg';
    return 'application/octet-stream';
  };

  const handleViewProof = async () => {
    if (!formData?.file && !formData?.file_key) return;

    try {
      setDownloadingProof(true);
      if (formData.file && formData.file_name) {
        const bytes = atob(formData.file);
        const byteNumbers = new Array(bytes.length);
        for (let i = 0; i < bytes.length; i += 1) {
          byteNumbers[i] = bytes.charCodeAt(i);
        }
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: getProofMimeType() });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        return;
      }

      const url = await getFileFromS3(formData.file_key, false);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      alert('Falha ao abrir o comprovante.');
    } finally {
      setDownloadingProof(false);
    }
  };

  useEffect(() => {
    if (formData?.procedimentosList) {
      const total = formData.procedimentosList.reduce((sum, proc) => sum + parseCurrencyValue(proc.valor), 0);
      if (total !== formData.valorTotal) {
        onUpdate({
          ...formData,
          valorTotal: total
        });
      }
    }
  }, [formData?.procedimentosList]);

  useEffect(() => {
    if (formData?.booking_channel) {
      if (formData.booking_channel.startsWith('Outro: ')) {
        setSelectedChannel('Outro');
        setOtherChannelText(formData.booking_channel.replace('Outro: ', ''));
      } else {
        setSelectedChannel(formData.booking_channel);
        setOtherChannelText('');
      }
    } else {
      setSelectedChannel('');
      setOtherChannelText('');
    }
  }, [formData?.booking_channel]);

  // console.log(formData);

  const handleEscapeKey = useCallback((event) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (showModal) {
      document.addEventListener('keydown', handleEscapeKey);
      fetchProcedimentos()
        .then(data => {
          const proceduresList = Object.entries(data.config.procedimentos).map(([key, value]) => ({
            name: key,
            ...value  
          }));
          setProcedures(proceduresList);

          const agentsMap = (data.config && data.config.booking_agents) || {};
          const agentsList = Object.entries(agentsMap).map(([name, info]) => ({
            id: info.id,
            name
          }));
          setBookingAgents(agentsList);
        })
        .catch(error => console.error('Error fetching procedures:', error));

      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [showModal, handleEscapeKey]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (paymentDropdownRef.current && !paymentDropdownRef.current.contains(e.target)) {
        setIsPaymentDropdownOpen(false);
      }
      if (flagsDropdownRef.current && !flagsDropdownRef.current.contains(e.target)) {
        setIsFlagsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProcedureSelect = (procedure) => {
    if (procedure.subcategorias) {
      setCurrentProcedure(procedure);
      setShowSubProcedures(true);
    } else {
      addProcedure(procedure.name, null, procedure.valor);
    }
  };

  const handleSubProcedureSelect = (subProcedureName, subProcedure) => {
    addProcedure(currentProcedure.name, subProcedureName, subProcedure.valor);
    setShowSubProcedures(false);
    setCurrentProcedure(null);
  };

  const addProcedure = (procedureName, subProcedureName, value) => {
    const newProcedure = {
      nome: subProcedureName ? `${procedureName} - ${subProcedureName}` : procedureName,
      valor: value
    };
    const updatedProcedures = [...(formData.procedimentosList || []), newProcedure];
    onUpdate({
      ...formData,
      procedimentosList: updatedProcedures,
      procedimentos: updatedProcedures.map(p => p.nome).join('|'),
      procedimentosDirty: true
    });
  };

  const updateProcedure = (index, field, value) => {
    const updatedProcedures = [...(formData.procedimentosList || [])];
    updatedProcedures[index] = {
      ...updatedProcedures[index],
      [field]: value
    };
    onUpdate({
      ...formData,
      procedimentosList: updatedProcedures,
      procedimentos: updatedProcedures.map(p => p.nome).join('|'),
      procedimentosDirty: true
    });
  };

  const removeProcedure = (index) => {
    const updatedProcedures = (formData.procedimentosList || []).filter((_, i) => i !== index);
    onUpdate({
      ...formData,
      procedimentosList: updatedProcedures,
      procedimentos: updatedProcedures.map(p => p.nome).join('|'),
      procedimentosDirty: true
    });
  };

  const filteredProcedures = procedures.filter(proc => 
    proc.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCourtesyChange = (isCourtesy) => {
    if (isCourtesy) {
      onUpdate({
        ...formData,
        paymentMethod: 'Cortesia',
        paymentStatus: 'Realizado',
        comissionStatus: 'Realizado',
        comission: '0',
        valorTotal: 0,
        status: 'Realizado'
      });
    } else {
      // If Cortesia is unchecked, reset payment method
      onUpdate({
        ...formData,
        paymentMethod: '',
        paymentStatus: '',
        comissionStatus: '',
        // Keep other values
      });
    }
  };

  if (!showModal || !formData) return null;

  const isCortesiaSelected = formData.paymentMethod === 'Cortesia';
  const isBloqueio = formData.status === 'Bloqueio';
  const chegouTimestamp = formatStatusTimestamp(formData.status_chegou_at);
  const realizadoTimestamp = formatStatusTimestamp(formData.status_realizado_at);
  const scheduledDate = parseDate(formData.dataAtendimento);
  const arrivedDate = parseDate(formData.status_chegou_at);
  const finalizedDate = parseDate(formData.status_realizado_at);
  const arrivalDelta = scheduledDate && arrivedDate
    ? arrivedDate - scheduledDate
    : null;
  const arrivalDeltaText = arrivalDelta !== null && Number.isFinite(arrivalDelta)
    ? formatDuration(arrivalDelta)
    : '';
  const arrivalDeltaLabel = arrivalDelta !== null && arrivalDelta < 0
    ? 'Paciente adiantado em'
    : 'Paciente atrasado em';
  const totalBookingTime = arrivedDate && finalizedDate
    ? formatDuration(finalizedDate - arrivedDate)
    : '';

  const statusOptions = [
    { key: 'confirmado', label: 'Confirmado por paciente', selected: formData.status === 'Confirmado', disabled: isLoading || formData.status === 'Chegou' || formData.status === 'Realizado' || isCortesiaSelected },
    { key: 'chegou', label: chegouTimestamp ? `Paciente chegou - ${chegouTimestamp}` : 'Paciente chegou', selected: formData.status === 'Chegou' || formData.status === 'Realizado', disabled: isLoading || formData.status === 'Realizado' || isCortesiaSelected },
    { key: 'finalizado', label: realizadoTimestamp ? `Atendimento finalizado - ${realizadoTimestamp}` : 'Atendimento finalizado', selected: formData.status === 'Realizado', disabled: isLoading || isCortesiaSelected },
    { key: 'cancelado', label: 'Agendamento cancelado', selected: formData.status === 'Cancelado', disabled: isLoading || isCortesiaSelected },
    { key: 'pago', label: 'Pagamento realizado', selected: formData.paymentStatus === 'Realizado' || isCortesiaSelected, disabled: isLoading || isCortesiaSelected },
  ];

  const toggleStatusOption = (key) => {
    if (key === 'confirmado') {
      const newVal = !(formData.status === 'Confirmado');
      onUpdate({ ...formData, status: newVal ? 'Confirmado' : 'Agendado' });
    } else if (key === 'chegou') {
      const newVal = !(formData.status === 'Chegou' || formData.status === 'Realizado');
      onUpdate({ ...formData, status: newVal ? 'Chegou' : 'Agendado' });
    } else if (key === 'finalizado') {
      const newVal = !(formData.status === 'Realizado');
      if (newVal) {
        onUpdate({ ...formData, status: 'Realizado' });
      } else {
        const arrived = (formData.status === 'Chegou' || formData.status === 'Realizado');
        onUpdate({ ...formData, status: arrived ? 'Chegou' : 'Agendado' });
      }
    } else if (key === 'cancelado') {
      const newVal = !(formData.status === 'Cancelado');
      onUpdate({ ...formData, status: newVal ? 'Cancelado' : 'Agendado' });
    } else if (key === 'pago') {
      const newVal = !(formData.paymentStatus === 'Realizado' || isCortesiaSelected);
      if (newVal) {
        if (!formData.paymentMethod && !isCortesiaSelected) {
          alert('Por favor, selecione o método de pagamento');
          return;
        }
        onUpdate({ ...formData, paymentStatus: 'Realizado', status: 'Realizado' });
      } else {
        onUpdate({ ...formData, paymentStatus: '' });
      }
    }
  };

  const copyBooking = () => {
    const proceduresList = (formData?.procedimentosList || []).map(p => {
      const name = p?.nome || '';
      const parts = name.split(' - ');
      return {
        procedure: parts[0] || '',
        subProcedure: parts[1] || null,
        value: parseCurrencyValue(p?.valor),
        commission: null
      };
    });
    const prefill = {
      professionalId: formData?.profissional || selectedBooking?.profissional,
      patient: selectedBooking ? {
        pacientId: selectedBooking.patientId || selectedBooking.pacientId,
        Nome: selectedBooking.paciente,
        paciente: selectedBooking.paciente
      } : undefined,
      selectedProcedures: proceduresList,
      duration: formData?.duration,
      observacao: formData?.observacao || '',
      roomId: selectedBooking?.room_id,
      booking_agent_name: formData?.booking_agent || '',
      booking_channel: formData?.booking_channel || ''
    };
    navigate('/agenda/criar', { state: { prefillFromBooking: prefill } });
  };

  return (
    <div 
      className="modal-overlay booking-modal"
    >
      <div 
        className="modal-content dark wide"
        onClick={e => e.stopPropagation()}
      >
        <div className="booking-edit-header">
          <div className="booking-edit-title-group">
            <div className="booking-edit-icon">
              <FaCalendarAlt />
            </div>
            <div>
              <div className="booking-edit-title-row">
                <h2>{isBloqueio ? 'Editar Bloqueio' : 'Editar Agendamento'}</h2>
                <span className={`booking-edit-status ${formData.status === 'Realizado' ? 'finished' : ''}`}>
                  {formData.status || 'Agendado'}
                </span>
              </div>
              <p>Atualize as informações da consulta e do pagamento.</p>
            </div>
          </div>
          <div className="booking-edit-header-actions">
            {!isBloqueio && (
              <button
                type="button"
                className="booking-icon-text-button"
                onClick={copyBooking}
                disabled={isLoading}
              >
                <FaCopy /> Copiar
              </button>
            )}
            <button
              type="button"
              className="booking-close-button"
              onClick={onClose}
              disabled={isLoading}
              aria-label="Fechar"
            >
              <FaTimes />
            </button>
          </div>
        </div>
        
        {isLoading ? (
          <div className="loading-container">
            <Spinner />
          </div>
        ) : isBloqueio ? (
          <>
            <div className="modal-form-grid">
              <div className="form-group grid-span-2">
                <label>Profissional</label>
                <input
                  type="text"
                  value={professionals.find(p => p.id === formData.profissional)?.name || 'Profissional'}
                  className="dark-input"
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label>Data e Hora</label>
                <input
                  type="text"
                  value={new Date(formData.dataAtendimento).toLocaleString('pt-BR')}
                  className="dark-input"
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label>Duração (minutos)</label>
                <input
                  type="text"
                  value={formData.duration}
                  className="dark-input"
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group grid-span-2">
                <label>Motivo do bloqueio</label>
                <textarea
                  value={formData.observacao || ''}
                  onChange={(e) => onUpdate({
                    ...formData,
                    observacao: e.target.value
                  })}
                  rows="4"
                  className="dark-input"
                  disabled={isLoading}
                  placeholder="Ex: Férias, Reunião, Consulta médica..."
                />
              </div>
            </div>

            <div className="modal-actions">
              <div className="left-buttons">
                <button 
                  className="action-button danger"
                  onClick={() => onUpdateStatus('Deletado')}
                  disabled={isLoading}
                  title="Exclui o bloqueio permanentemente"
                >
                  !! Deletar !!
                </button>
              </div>
              <div className="right-buttons">
                <button 
                  className="action-button"
                  onClick={() => onUpdateStatus()}
                  disabled={isLoading}
                >
                  Salvar Alterações
                </button>
                <button 
                  className="action-button secondary"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Fechar
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="booking-edit-body">
              <section className="booking-edit-section">
                <h3><FaInfoCircle /> Informações principais</h3>
                <div className="booking-edit-grid">
                  <div className="form-group">
                    <label>Responsável pelo agendamento</label>
                    <div className="input-with-icon">
                      <FaUser />
                      <select
                        className="dark-input"
                        value={formData.booking_agent || ''}
                        onChange={(e) => onUpdate({
                          ...formData,
                          booking_agent: e.target.value
                        })}
                        disabled={isLoading}
                      >
                        <option value="">Selecione um responsável</option>
                        {bookingAgents.map(agent => (
                          <option key={agent.name} value={agent.name}>{agent.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Canal do agendamento</label>
                    <div className="input-with-icon">
                      <FaPhoneAlt />
                      <select
                        className="dark-input"
                        value={selectedChannel}
                        onChange={(e) => {
                          const newChannel = e.target.value;
                          setSelectedChannel(newChannel);
                          const bookingChannel = newChannel === 'Outro' ? `Outro: ${otherChannelText || ''}` : (newChannel || null);
                          onUpdate({
                            ...formData,
                            booking_channel: bookingChannel
                          });
                        }}
                        disabled={isLoading}
                      >
                        <option value="">Selecione um canal</option>
                        <option value="Whatsapp">Whatsapp</option>
                        <option value="Telefone">Telefone</option>
                        <option value="Ao vivo">Ao vivo</option>
                        <option value="Instagram">Instagram</option>
                        <option value="Facebook">Facebook</option>
                        <option value="Indicação">Indicação</option>
                        <option value="Site">Site</option>
                        <option value="Outro">Outro</option>
                      </select>
                    </div>
                    {selectedChannel === 'Outro' && (
                      <input
                        type="text"
                        className="dark-input"
                        placeholder="Especifique o canal"
                        value={otherChannelText}
                        onChange={(e) => {
                          const text = e.target.value;
                          setOtherChannelText(text);
                          const bookingChannel = `Outro: ${text || ''}`;
                          onUpdate({
                            ...formData,
                            booking_channel: bookingChannel
                          });
                        }}
                        disabled={isLoading}
                      />
                    )}
                  </div>

                  <div className="form-group">
                    <label>Data</label>
                    <div className="input-with-icon">
                      <FaCalendarAlt />
                      <input
                        type="datetime-local"
                        value={formData.dataAtendimento.slice(0, 16)}
                        onChange={(e) => onUpdate({
                          ...formData,
                          dataAtendimento: e.target.value + ':00Z'
                        })}
                        className="dark-input"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Hora e duração</label>
                    <div className="booking-edit-inline-fields">
                      <div className="input-with-icon">
                        <FaClock />
                        <input
                          type="text"
                          inputMode="decimal"
                          value={formData.duration}
                          onChange={(e) => {
                            const value = e.target.value.replace(',', '.');
                            onUpdate({
                              ...formData,
                              duration: value
                            });
                          }}
                          className="dark-input"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group grid-span-2">
                    <label>Profissional</label>
                    <div className="input-with-icon">
                      <FaUser />
                      <select
                        className="dark-input"
                        value={formData.profissional || ''}
                        onChange={(e) => onUpdate({
                          ...formData,
                          profissional: e.target.value
                        })}
                        disabled={isLoading}
                      >
                        <option value="">Selecione um profissional</option>
                        {professionals && professionals.map((professional) => (
                          <option key={professional.id} value={professional.id}>
                            {professional.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </section>

              <section className="booking-edit-section" ref={flagsDropdownRef}>
                <h3><FaCheckCircle /> Status do atendimento</h3>
                <div className="booking-status-chips">
                  {statusOptions.filter(opt => opt.selected).map(opt => (
                    <button
                      type="button"
                      key={opt.key}
                      className={`booking-status-chip selected ${opt.key}`}
                      onClick={() => !opt.disabled && toggleStatusOption(opt.key)}
                      disabled={opt.disabled}
                    >
                      <FaCheckCircle /> {opt.label} <FaTimes />
                    </button>
                  ))}
                </div>
                <div className="status-multiselect booking-add-status">
                  <button
                    type="button"
                    className={`status-trigger ${isFlagsDropdownOpen ? 'open' : ''}`}
                    onClick={() => setIsFlagsDropdownOpen(!isFlagsDropdownOpen)}
                  >
                    <FaPlus /> Adicionar status
                  </button>
                  {isFlagsDropdownOpen && (
                    <div className="status-dropdown">
                      {statusOptions.map(opt => (
                        <label key={opt.key} className="status-option" style={opt.disabled ? { opacity: 0.6, pointerEvents: 'none' } : {}}>
                          <input
                            type="checkbox"
                            checked={opt.selected}
                            onChange={() => toggleStatusOption(opt.key)}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {(chegouTimestamp || realizadoTimestamp || arrivalDeltaText || totalBookingTime) && (
                  <div className="booking-edit-grid booking-timing-grid">
                    {chegouTimestamp && (
                      <div className="form-group">
                        <label>Paciente Chegou</label>
                        <input
                          type="text"
                          value={chegouTimestamp}
                          className="dark-input"
                          disabled
                          readOnly
                        />
                      </div>
                    )}

                    {arrivalDeltaText && (
                      <div className="form-group">
                        <label>{arrivalDeltaLabel}</label>
                        <input
                          type="text"
                          value={arrivalDeltaText}
                          className="dark-input"
                          disabled
                          readOnly
                        />
                      </div>
                    )}

                    {realizadoTimestamp && (
                      <div className="form-group">
                        <label>Atendimento Finalizado</label>
                        <input
                          type="text"
                          value={realizadoTimestamp}
                          className="dark-input"
                          disabled
                          readOnly
                        />
                      </div>
                    )}

                    {totalBookingTime && (
                      <div className="form-group">
                        <label>Tempo Total do Atendimento</label>
                        <input
                          type="text"
                          value={totalBookingTime}
                          className="dark-input"
                          disabled
                          readOnly
                        />
                      </div>
                    )}
                  </div>
                )}
              </section>

              <section className="booking-edit-section">
                <h3><FaCalendarAlt /> Procedimentos</h3>
                <div className="procedure-search input-with-icon">
                  <FaSearch />
                  <input
                    type="text"
                    placeholder="Buscar procedimentos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="procedure-search-input"
                  />
                </div>
                
                {(searchTerm || showSubProcedures) && (!showSubProcedures ? (
                  <div className="procedure-list">
                    {filteredProcedures.map((proc) => (
                      <div 
                        key={proc.name}
                        className="procedure-item"
                        onClick={() => handleProcedureSelect(proc)}
                      >
                        {proc.name}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="subprocedure-list">
                    <div className="subprocedure-header">
                      <button onClick={() => setShowSubProcedures(false)}>Voltar</button>
                      <h4>{currentProcedure.name}</h4>
                    </div>
                    {Object.entries(currentProcedure.subcategorias).map(([key, value]) => (
                      <div 
                        key={key}
                        className="subprocedure-item"
                        onClick={() => handleSubProcedureSelect(key, value)}
                      >
                        {key} - R$ {value.valor}
                      </div>
                    ))}
                  </div>
                ))}

                <div className="selected-procedures">
                  {(formData.procedimentosList || []).map((proc, index) => (
                    <div key={index} className="selected-procedure-item">
                      <div className="procedure-details">
                        <span>{proc.nome}</span>
                        <div className="procedure-value-edit">
                          <label>Valor R$</label>
                          <input 
                            type="text"
                            inputMode="decimal"
                            value={proc.valor} 
                            onChange={(e) => {
                              updateProcedure(index, 'valor', e.target.value);
                            }}
                            className="dark-input"
                            disabled={isCortesiaSelected}
                          />
                        </div>
                      </div>
                      <button className="remove-procedure-button" onClick={() => removeProcedure(index)}>
                        <FaTrashAlt />
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" className="booking-add-row-button" onClick={() => setSearchTerm(searchTerm || ' ')}>
                  <FaPlus /> Adicionar procedimento
                </button>
              </section>

              <section className="booking-edit-section">
                <h3><FaDollarSign /> Resumo financeiro</h3>
                <div className="booking-financial-grid">
                  <div className="financial-total">
                    <label>Valor Total (R$)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.valorTotal !== null && formData.valorTotal !== undefined ? formData.valorTotal : ''}
                      onChange={(e) => {
                        onUpdate({
                          ...formData,
                          valorTotal: e.target.value
                        });
                      }}
                      className="dark-input financial-total-input"
                      disabled={isLoading || isCortesiaSelected}
                    />
                  </div>

                  <div className="form-group" ref={paymentDropdownRef}>
                    <label>Modo de Pagamento</label>
                    <div className="status-multiselect">
                      <button
                        type="button"
                        className={`status-trigger ${isPaymentDropdownOpen ? 'open' : ''}`}
                        onClick={() => setIsPaymentDropdownOpen(!isPaymentDropdownOpen)}
                        disabled={isLoading}
                      >
                        {(() => {
                          const methods = (formData.paymentMethod || '').split(', ').filter(Boolean);
                          return methods.length ? methods.join(', ') : 'Selecionar métodos';
                        })()}
                      </button>
                      {isPaymentDropdownOpen && (
                        <div className="status-dropdown">
                          {['Pix', 'Crédito', 'Débito', 'Dinheiro', 'Cortesia', 'Plano de saúde'].map(method => {
                            const selected = method === 'Cortesia' ? isCortesiaSelected : (formData.paymentMethod || '').split(', ').includes(method);
                            const disabled = isLoading || (method !== 'Cortesia' && isCortesiaSelected);
                            return (
                              <label key={method} className="status-option" style={disabled ? { opacity: 0.6, pointerEvents: 'none' } : {}}>
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    if (method === 'Cortesia') {
                                      handleCourtesyChange(isChecked);
                                      return;
                                    }
                                    if (isCortesiaSelected && isChecked) {
                                      alert('Por favor, desmarque Cortesia antes de selecionar outro método de pagamento');
                                      return;
                                    }
                                    const currentMethods = (formData.paymentMethod || '').split(', ').filter(m => m !== '');
                                    let newMethods;
                                    if (isChecked) {
                                      newMethods = [...currentMethods, method];
                                    } else {
                                      newMethods = currentMethods.filter(m => m !== method);
                                    }
                                    onUpdate({
                                      ...formData,
                                      paymentMethod: newMethods.join(', '),
                                      ...(method === 'Crédito' && isChecked && !formData.payment_installments ? { payment_installments: 1 } : {}),
                                      ...(method === 'Crédito' && !isChecked ? { payment_installments: undefined } : {}),
                                      paymentStatus: newMethods.length > 0 ? 'Realizado' : ''
                                    });
                                  }}
                                />
                                <span>{method}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {(formData.paymentStatus === 'Realizado' && !isCortesiaSelected) && (
                    <div className="form-group">
                      <label>Data do Pagamento</label>
                      <div className="input-with-icon">
                        <FaCalendarAlt />
                        <input
                          type="datetime-local"
                          value={(formData.paymentDate || formData.dataAtendimento).slice(0, 16)}
                          onChange={(e) => onUpdate({
                            ...formData,
                            paymentDate: e.target.value + ':00Z'
                          })}
                          className="dark-input"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  )}

                  {(formData.paymentStatus === 'Realizado' && !isCortesiaSelected) && (
                    <div className="file-upload-wrapper financial-proof-upload">
                      <input
                        type="file"
                        id="payment-proof"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setUploadingProof(true);
                            const uploadStart = Date.now();
                            const reader = new FileReader();
                            reader.onloadend = async () => {
                              const base64 = reader.result.split(',')[1];
                              const nextFormData = {
                                ...formData,
                                file: base64,
                                file_name: file.name,
                                proofSaved: false
                              };
                              onUpdate(nextFormData);
                              try {
                                await onUpdateStatus(undefined, nextFormData, { keepModalOpen: true });
                                const elapsed = Date.now() - uploadStart;
                                const remaining = Math.max(300 - elapsed, 0);
                                setTimeout(() => setUploadingProof(false), remaining);
                              } catch (error) {
                                alert('Falha ao anexar o comprovante.');
                                const elapsed = Date.now() - uploadStart;
                                const remaining = Math.max(300 - elapsed, 0);
                                setTimeout(() => setUploadingProof(false), remaining);
                              }
                            };
                            reader.onerror = () => {
                              const elapsed = Date.now() - uploadStart;
                              const remaining = Math.max(300 - elapsed, 0);
                              setTimeout(() => setUploadingProof(false), remaining);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        accept=".pdf, .jpg, .jpeg, .png"
                        style={{ display: 'none' }}
                        disabled={isLoading || uploadingProof}
                      />
                      <label
                        htmlFor={uploadingProof ? undefined : 'payment-proof'}
                        className="file-upload-label action-button-like"
                        aria-disabled={uploadingProof ? 'true' : 'false'}
                      >
                        {uploadingProof ? (
                          <>
                            <span className="btn-spinner" aria-hidden="true"></span>
                            Anexando...
                          </>
                        ) : (
                          <>
                            <FaFileUpload /> {(formData.file_name || formData.file_key) ? 'Mudar comprovante' : 'Anexar comprovante'}
                          </>
                        )}
                      </label>
                      {(formData.file || formData.file_key) && (
                        <button
                          type="button"
                          className="proof-link-button"
                          onClick={handleViewProof}
                          disabled={isLoading || uploadingProof || downloadingProof}
                          title="Ver comprovante anexado"
                        >
                          {downloadingProof ? (
                            <>
                              <span className="btn-spinner" aria-hidden="true"></span>
                              Abrindo...
                            </>
                          ) : (
                            <>
                              <FaExternalLinkAlt />
                              <span className="proof-link-text">{getProofFileName()}</span>
                            </>
                          )}
                        </button>
                      )}
                      {formData.file && !formData.proofSaved && !uploadingProof && (
                        <div className="unsaved-note">Não esqueça de salvar as alterações</div>
                      )}
                    </div>
                  )}
                </div>

                {(formData.paymentMethod || '').split(', ').includes('Crédito') && (
                  <div className="credit-installment-row">
                    <div className={`payment-method-option ${formData.payment_installments === 1 ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="credit-installments-type"
                        id="installment-avista"
                        checked={formData.payment_installments === 1}
                        onChange={() => onUpdate({ ...formData, payment_installments: 1 })}
                        disabled={isLoading}
                      />
                      <label htmlFor="installment-avista">A vista</label>
                    </div>
                    <div className={`payment-method-option ${(formData.payment_installments || 0) > 1 ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="credit-installments-type"
                        id="installment-parcelado"
                        checked={(formData.payment_installments || 0) > 1}
                        onChange={() => onUpdate({ ...formData, payment_installments: Math.min(Math.max(formData.payment_installments || 2, 2), 10) })}
                        disabled={isLoading}
                      />
                      <label htmlFor="installment-parcelado">Parcelado</label>
                    </div>
                    {(formData.payment_installments || 0) > 1 && (
                      <select
                        className="dark-input installment-select"
                        value={Math.min(Math.max(formData.payment_installments || 2, 2), 10)}
                        onChange={(e) => onUpdate({ ...formData, payment_installments: Math.min(Math.max(parseInt(e.target.value, 10) || 2, 2), 10) })}
                        disabled={isLoading}
                      >
                        {Array.from({ length: 9 }, (_, i) => i + 2).map(n => (
                          <option key={n} value={n}>{`${n}x`}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                <div className={`payment-status-line ${formData.paymentStatus === 'Realizado' || isCortesiaSelected ? 'paid' : ''}`}>
                  Status do pagamento
                  <span>{formData.paymentStatus === 'Realizado' || isCortesiaSelected ? 'Pago' : 'Pendente'} <FaCheckCircle /></span>
                </div>
              </section>

              <section className="booking-edit-section">
                <h3><FaInfoCircle /> Observações</h3>
                <div className="booking-edit-grid">
                  <div className="form-group grid-span-2">
                    <label>Observação</label>
                    <textarea
                      value={formData.observacao || ''}
                      onChange={(e) => onUpdate({
                        ...formData,
                        observacao: e.target.value
                      })}
                      rows="3"
                      className="dark-input"
                      disabled={isLoading}
                      placeholder="Adicione observações sobre o agendamento..."
                    />
                  </div>
                  <div className="form-group grid-span-2">
                    <label>Observação para financeiro</label>
                    <textarea
                      value={formData.financeiro_observacao || ''}
                      onChange={(e) => onUpdate({
                        ...formData,
                        financeiro_observacao: e.target.value
                      })}
                      rows="3"
                      className="dark-input"
                      disabled={isLoading}
                      placeholder="Adicione observações para o financeiro..."
                    />
                  </div>
                </div>
              </section>
            </div>

            <div className="modal-actions">
              <div className="left-buttons">
                <button 
                  className="action-button danger"
                  onClick={() => onUpdateStatus('Deletado')}
                  disabled={isLoading}
                  title="!! Deletar !! Exclui o agendamento permanentemente"
                >
                  <FaTrashAlt /> Excluir agendamento
                </button>
              </div>
              <div className="right-buttons">
                <button 
                  className="action-button secondary"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Fechar
                </button>
                <button 
                  className="action-button primary"
                  onClick={() => onUpdateStatus()}
                  disabled={isLoading}
                >
                  <FaSave /> Salvar Alterações
                </button>
              </div>
            </div>

            <style>
              {`
                .checkbox-container {
                  display: flex;
                  flex-direction: column;
                  gap: 8px;
                  align-items: flex-start;
                }

                .checkbox-label {
                  font-weight: 500;
                  color: #333;
                }

                .custom-checkbox {
                  position: relative;
                  display: inline-block;
                  cursor: pointer;
                }

                .custom-checkbox input {
                  position: absolute;
                  opacity: 0;
                  cursor: pointer;
                  height: 24px;
                  width: 24px;
                  top: 0;
                  left: 0;
                  margin: 0;
                  z-index: 1;
                }

                .checkmark {
                  position: relative;
                  display: inline-block;
                  height: 24px;
                  width: 24px;
                  background-color: #2c2c2c;
                  border: 2px solid #4a4a4a;
                  border-radius: 4px;
                  transition: all 0.2s ease;
                  pointer-events: none;
                }

                .custom-checkbox:hover .checkmark {
                  background-color: #3c3c3c;
                }

                .custom-checkbox input:checked ~ .checkmark {
                  background-color: #4a90e2;
                  border-color: #4a90e2;
                }

                .checkmark:after {
                  content: "";
                  position: absolute;
                  display: none;
                  left: 8px;
                  top: 4px;
                  width: 5px;
                  height: 10px;
                  border: solid white;
                  border-width: 0 2px 2px 0;
                  transform: rotate(45deg);
                }

                .custom-checkbox input:checked ~ .checkmark:after {
                  display: block;
                }

                .custom-checkbox input:disabled ~ .checkmark {
                  opacity: 0.5;
                  cursor: not-allowed;
                }

                .modal-actions {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-top: 20px;
                }

                .left-buttons {
                  display: flex;
                  gap: 10px;
                  align-items: center;
                }

                .right-buttons {
                  display: flex;
                  gap: 10px;
                }

                .file-upload-wrapper {
                  margin-top: 8px;
                  display: flex;
                  align-items: center;
                  gap: 10px;
                  flex-wrap: wrap;
                }

                .file-upload-label {
                  display: inline-flex;
                  align-items: center;
                  gap: 8px;
                  padding: 8px 16px;
                  background-color: #2c2c2c;
                  border: 2px solid #4a4a4a;
                  border-radius: 4px;
                  cursor: pointer;
                  transition: all 0.2s ease;
                  color: #e0e0e0;
                }

                .file-upload-label:hover {
                  background-color: #3c3c3c;
                }
                .action-button-like {
                  display: inline-flex;
                  align-items: center;
                  gap: 8px;
                  padding: 8px 16px;
                  background-color: #2c2c2c;
                  border: 2px solid #4a4a4a;
                  border-radius: 4px;
                  color: #e0e0e0;
                  cursor: pointer;
                  transition: all 0.2s ease;
                  font: inherit;
                  line-height: 1.2;
                  -webkit-appearance: none;
                  appearance: none;
                  text-decoration: none;
                }

                .action-button-like:hover {
                  background-color: #3c3c3c;
                }

                .action-button-like svg {
                  font-size: 1.2em;
                }

                .btn-spinner {
                  width: 16px;
                  height: 16px;
                  border: 2px solid rgba(255,255,255,0.3);
                  border-top-color: #ffffff;
                  border-radius: 50%;
                  display: inline-block;
                  animation: spin 0.8s linear infinite;
                  margin-right: 8px;
                }

                @keyframes spin {
                  to { transform: rotate(360deg); }
                }

                .file-upload-label:disabled,
                .action-button-like:disabled,
                .file-upload-label[aria-disabled="true"],
                .action-button-like[aria-disabled="true"] {
                  opacity: 0.6;
                  cursor: not-allowed;
                  pointer-events: none;
                }

                .file-upload-label svg {
                  font-size: 1.2em;
                }

                .payment-methods-container {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 12px;
                  margin-top: 8px;
                }

                .unsaved-note {
                  margin-top: 6px;
                  font-size: 12px;
                  color: #ffcc66;
                }

                .payment-method-option {
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  flex: 0 0 auto;
                  min-width: 128px;
                  background-color: #2c2c2c;
                  border: 1px solid #4a4a4a;
                  border-radius: 8px;
                  padding: 10px 16px;
                  cursor: pointer;
                  transition: all 0.2s ease;
                  position: relative;
                  overflow: visible;
                }

                .payment-method-option:hover {
                  background-color: #3c3c3c;
                  transform: translateY(-2px);
                  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
                }

                .payment-method-option.selected {
                  background-color: #2d4263;
                  border-color: #4a90e2;
                  box-shadow: 0 0 0 1px #4a90e2, 0 4px 8px rgba(0, 0, 0, 0.2);
                }

                .payment-method-option input[type="checkbox"],
                .payment-method-option input[type="radio"] {
                  margin: 0;
                  accent-color: #4a90e2;
                  height: 18px;
                  width: 18px;
                  flex: 0 0 18px;
                }

                .payment-method-option label {
                  cursor: pointer;
                  margin: 0;
                  font-weight: 500;
                  color: #e0e0e0;
                  font-size: 14px;
                  letter-spacing: 0.3px;
                  width: auto;
                  max-width: none;
                  white-space: nowrap;
                  overflow: visible;
                  display: inline-flex;
                  flex: 0 0 auto;
                }

                .payment-method-option:disabled {
                  opacity: 0.6;
                  cursor: not-allowed;
                }
              `}
            </style>
          </>
        )}
      </div>
    </div>
  );
}

export default EditBookingModal; 
